<script setup>
import { ref, computed, toRef, watch } from 'vue';
import { useAlert } from 'dashboard/composables';
import { useFunctionGetter, useStore } from 'dashboard/composables/store';
import {
  COMPONENT_TYPES,
  findComponentByType,
} from 'dashboard/helper/templateHelper';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  inboxId: {
    type: Number,
    default: undefined,
  },
  requestContactInfoOnly: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['onSelect']);

const { t } = useI18n();
const store = useStore();

const query = ref('');
const isRefreshing = ref(false);
const highlightedTemplateKey = ref('');

const whatsAppTemplateMessages = useFunctionGetter(
  'inboxes/getFilteredWhatsAppTemplates',
  toRef(props, 'inboxId')
);

const hasRequestContactInfoButton = template => {
  const buttons = findComponentByType(template, COMPONENT_TYPES.BUTTONS);

  return buttons?.buttons?.some(
    button => button.type?.toUpperCase() === 'REQUEST_CONTACT_INFO'
  );
};

const availableTemplateMessages = computed(() => {
  if (!props.requestContactInfoOnly) {
    return whatsAppTemplateMessages.value;
  }

  return whatsAppTemplateMessages.value.filter(hasRequestContactInfoButton);
});

const getTemplateBody = template =>
  findComponentByType(template, COMPONENT_TYPES.BODY)?.text || '';

const normalizeSearchValue = value =>
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

const filteredTemplateMessages = computed(() => {
  const normalizedQuery = normalizeSearchValue(query.value.trim());

  if (!normalizedQuery) return availableTemplateMessages.value;

  return availableTemplateMessages.value.filter(template => {
    const searchableContent = [
      template.name,
      template.language,
      template.category,
      getTemplateBody(template),
    ]
      .map(normalizeSearchValue)
      .join(' ');

    return searchableContent.includes(normalizedQuery);
  });
});

const languageLabel = language => {
  const labels = {
    pt_BR: 'Português',
    pt_PT: 'Português',
    en: 'Inglês',
    en_US: 'Inglês',
    en_GB: 'Inglês',
    es: 'Espanhol',
    es_ES: 'Espanhol',
    es_MX: 'Espanhol',
  };

  return labels[language] || language || 'Idioma';
};

const categoryLabel = category => {
  const labels = {
    MARKETING: 'Marketing',
    UTILITY: 'Utilidade',
    AUTHENTICATION: 'Autenticação',
    TICKET_UPDATE: 'Atualização',
    ISSUE_RESOLUTION: 'Atendimento',
    SHIPPING_UPDATE: 'Atualização',
  };

  return labels[category] || category || 'Modelo';
};

const formatTemplateName = name => {
  if (!name) return 'Modelo de mensagem';

  return name
    .replace(/_/g, ' ')
    .replace(/\b\w/g, character => character.toUpperCase());
};

const templateKey = template =>
  `${template.name || 'template'}-${template.language || 'language'}`;

const highlightedTemplate = computed(() => {
  const templates = filteredTemplateMessages.value;

  if (!templates.length) return null;

  return (
    templates.find(
      template => templateKey(template) === highlightedTemplateKey.value
    ) || templates[0]
  );
});

const highlightTemplate = template => {
  highlightedTemplateKey.value = templateKey(template);
};

const useTemplate = () => {
  if (highlightedTemplate.value) {
    emit('onSelect', highlightedTemplate.value);
  }
};

watch(
  filteredTemplateMessages,
  templates => {
    if (!templates.length) {
      highlightedTemplateKey.value = '';
      return;
    }

    const stillExists = templates.some(
      template => templateKey(template) === highlightedTemplateKey.value
    );

    if (!stillExists) {
      highlightedTemplateKey.value = templateKey(templates[0]);
    }
  },
  { immediate: true }
);

const refreshTemplates = async () => {
  isRefreshing.value = true;

  try {
    await store.dispatch('inboxes/syncTemplates', props.inboxId);
    useAlert(t('WHATSAPP_TEMPLATES.PICKER.REFRESH_SUCCESS'));
  } catch (error) {
    useAlert(t('WHATSAPP_TEMPLATES.PICKER.REFRESH_ERROR'));
  } finally {
    isRefreshing.value = false;
  }
};
</script>

<template>
  <div class="mayara-picker">
    <!-- Coluna esquerda -->
    <section class="mayara-picker-sidebar">
      <div class="mayara-picker-toolbar">
        <div class="mayara-search">
          <Icon icon="i-lucide-search" class="mayara-search-icon" />
          <input
            v-model="query"
            type="search"
            placeholder="Buscar modelo ou conteúdo..."
            class="mayara-search-input"
          />
        </div>

        <button
          type="button"
          :disabled="isRefreshing"
          class="mayara-refresh"
          :title="t('WHATSAPP_TEMPLATES.PICKER.REFRESH_BUTTON')"
          @click="refreshTemplates"
        >
          <Icon
            icon="i-lucide-refresh-ccw"
            class="size-4"
            :class="{ 'animate-spin': isRefreshing }"
          />
        </button>
      </div>

      <div class="mayara-list-summary">
        <span>Modelos aprovados</span>
        <span>{{ filteredTemplateMessages.length }}</span>
      </div>

      <div
        v-if="filteredTemplateMessages.length"
        class="mayara-template-list"
      >
        <button
          v-for="template in filteredTemplateMessages"
          :key="templateKey(template)"
          type="button"
          class="mayara-template-row"
          :class="{
            'mayara-template-row-active':
              templateKey(template) === templateKey(highlightedTemplate),
          }"
          @click="highlightTemplate(template)"
          @dblclick="emit('onSelect', template)"
        >
          <div class="mayara-row-main">
            <p class="mayara-template-name">
              {{ formatTemplateName(template.name) }}
            </p>

            <p class="mayara-template-technical-name">
              {{ template.name }}
            </p>
          </div>

          <Icon
            icon="i-lucide-chevron-right"
            class="mayara-row-chevron"
          />

          <div class="mayara-row-badges">
            <span class="mayara-badge mayara-badge-language">
              {{ languageLabel(template.language) }}
            </span>
            <span class="mayara-badge mayara-badge-category">
              {{ categoryLabel(template.category) }}
            </span>
          </div>

          <p class="mayara-row-preview">
            {{ getTemplateBody(template) || 'Modelo sem texto de prévia.' }}
          </p>
        </button>
      </div>

      <div
        v-else-if="query && availableTemplateMessages.length"
        class="mayara-empty-state"
      >
        <div class="mayara-empty-icon">
          <Icon icon="i-lucide-search-x" class="size-5" />
        </div>
        <p class="mayara-empty-title">Nenhum modelo encontrado</p>
        <p class="mayara-empty-description">
          Nenhuma mensagem corresponde a
          <strong>“{{ query }}”</strong>.
        </p>
      </div>

      <div v-else class="mayara-empty-state">
        <div class="mayara-empty-icon">
          <Icon icon="i-lucide-message-square-text" class="size-5" />
        </div>
        <p class="mayara-empty-title">Nenhum modelo disponível</p>
        <p class="mayara-empty-description">
          Atualize a lista para sincronizar os modelos aprovados.
        </p>

        <button
          type="button"
          :disabled="isRefreshing"
          class="mayara-empty-refresh"
          @click="refreshTemplates"
        >
          <Icon
            icon="i-lucide-refresh-ccw"
            class="size-4"
            :class="{ 'animate-spin': isRefreshing }"
          />
          Atualizar modelos
        </button>
      </div>
    </section>

    <!-- Coluna direita -->
    <section class="mayara-picker-preview">
      <div
        v-if="highlightedTemplate"
        class="mayara-preview-layout"
      >
        <div class="mayara-preview-header">
          <div>
            <p class="mayara-preview-eyebrow">
              Prévia do modelo
            </p>
            <h4 class="mayara-preview-title">
              {{ formatTemplateName(highlightedTemplate.name) }}
            </h4>
            <p class="mayara-preview-technical-name">
              {{ highlightedTemplate.name }}
            </p>
          </div>

          <div class="mayara-preview-badges">
            <span class="mayara-badge mayara-badge-language">
              {{ languageLabel(highlightedTemplate.language) }}
            </span>
            <span class="mayara-badge mayara-badge-category">
              {{ categoryLabel(highlightedTemplate.category) }}
            </span>
          </div>
        </div>

        <div class="mayara-phone-stage">
          <div class="mayara-phone-preview">
            <div class="mayara-whatsapp-topline">
              <span class="mayara-whatsapp-dot" />
              WhatsApp
            </div>

            <div class="mayara-message-bubble">
              <p>
                {{
                  getTemplateBody(highlightedTemplate) ||
                  'Este modelo não possui texto de prévia.'
                }}
              </p>
            </div>

            <p class="mayara-preview-note">
              Campos dinâmicos serão preenchidos na próxima etapa.
            </p>
          </div>
        </div>

        <div class="mayara-preview-actions">
          <div class="mayara-action-help">
            <Icon icon="i-lucide-shield-check" class="size-4" />
            <span>Modelo aprovado pelo WhatsApp</span>
          </div>

          <button
            type="button"
            class="mayara-use-template"
            @click="useTemplate"
          >
            <span>Usar este modelo</span>
            <Icon icon="i-lucide-arrow-right" class="size-4" />
          </button>
        </div>
      </div>

      <div v-else class="mayara-preview-empty">
        <Icon icon="i-lucide-message-square-dashed" class="size-7" />
        <p>Selecione um modelo para visualizar a prévia.</p>
      </div>
    </section>
  </div>
</template>

<style scoped>
.mayara-picker {
  display: grid;
  grid-template-columns: minmax(17rem, 36%) minmax(0, 64%);
  width: 100%;
  height: min(66vh, 42rem);
  min-height: 31rem;
  overflow: hidden;
  border: 1px solid rgb(var(--slate-5));
  border-radius: 1rem;
  background: rgb(var(--slate-1));
}

.mayara-picker-sidebar {
  display: flex;
  min-width: 0;
  min-height: 0;
  flex-direction: column;
  padding: 0.875rem;
  border-right: 1px solid rgb(var(--slate-5));
  background: rgb(var(--slate-2));
}

.mayara-picker-toolbar {
  display: flex;
  align-items: center;
  gap: 0.625rem;
}

.mayara-search {
  display: flex;
  flex: 1;
  align-items: center;
  gap: 0.5rem;
  height: 2.5rem;
  min-width: 0;
  padding: 0 0.75rem;
  border: 1px solid rgb(var(--slate-5));
  border-radius: 0.75rem;
  background: rgb(var(--slate-1));
}

.mayara-search:focus-within {
  border-color: rgb(var(--green-8));
  box-shadow: 0 0 0 3px rgb(var(--green-5) / 0.14);
}

.mayara-search-icon {
  flex: 0 0 auto;
  width: 1rem;
  height: 1rem;
  color: rgb(var(--slate-10));
}

.mayara-search-input {
  width: 100%;
  height: 100%;
  min-width: 0;
  margin: 0;
  padding: 0 !important;
  border: 0 !important;
  border-radius: 0 !important;
  outline: 0 !important;
  appearance: none;
  -webkit-appearance: none;
  background: transparent !important;
  box-shadow: none !important;
  color: rgb(var(--slate-12));
  font: inherit;
  font-size: 0.8125rem;
}

.mayara-search-input:focus,
.mayara-search-input:focus-visible {
  border: 0 !important;
  outline: 0 !important;
  background: transparent !important;
  box-shadow: none !important;
}

.mayara-search-input::-webkit-search-decoration,
.mayara-search-input::-webkit-search-cancel-button,
.mayara-search-input::-webkit-search-results-button,
.mayara-search-input::-webkit-search-results-decoration {
  -webkit-appearance: none;
}

.mayara-search-input::placeholder {
  color: rgb(var(--slate-9));
}

.mayara-search input.mayara-search-input,
.mayara-search input.mayara-search-input:hover,
.mayara-search input.mayara-search-input:focus,
.mayara-search input.mayara-search-input:focus-visible,
.mayara-search input.mayara-search-input:active {
  margin: 0 !important;
  padding: 0 !important;
  border: 0 !important;
  border-width: 0 !important;
  border-color: transparent !important;
  border-radius: 0 !important;
  outline: 0 !important;
  outline-offset: 0 !important;
  background: transparent !important;
  box-shadow: none !important;
  --tw-ring-offset-width: 0px !important;
  --tw-ring-offset-color: transparent !important;
  --tw-ring-color: transparent !important;
  --tw-ring-offset-shadow: 0 0 #0000 !important;
  --tw-ring-shadow: 0 0 #0000 !important;
}

.mayara-refresh {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 2.5rem;
  height: 2.5rem;
  border: 1px solid rgb(var(--slate-5));
  border-radius: 0.75rem;
  background: rgb(var(--slate-1));
  color: rgb(var(--slate-11));
  cursor: pointer;
}

.mayara-refresh:hover:not(:disabled) {
  background: rgb(var(--slate-3));
}

.mayara-refresh:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

.mayara-list-summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.875rem 0.25rem 0.625rem;
  color: rgb(var(--slate-9));
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.mayara-list-summary span:last-child {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.5rem;
  height: 1.5rem;
  padding: 0 0.375rem;
  border-radius: 999px;
  background: rgb(var(--slate-4));
  color: rgb(var(--slate-11));
}

.mayara-template-list {
  display: flex;
  min-height: 0;
  flex: 1;
  flex-direction: column;
  gap: 0.375rem;
  padding: 0.125rem 0.25rem 0.125rem 0;
  overflow-y: auto;
}

.mayara-template-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 0.375rem 0.625rem;
  width: 100%;
  padding: 0.75rem;
  border: 1px solid rgb(var(--slate-4));
  border-radius: 0.875rem;
  background: rgb(var(--slate-1));
  box-shadow: 0 1px 2px rgb(0 0 0 / 0.025);
  color: rgb(var(--slate-12));
  text-align: left;
  cursor: pointer;
  transition:
    transform 120ms ease,
    background 120ms ease,
    border-color 120ms ease,
    box-shadow 120ms ease;
}

.mayara-template-row:hover {
  border-color: rgb(var(--slate-6));
  background: rgb(var(--slate-2));
  box-shadow: 0 3px 10px rgb(0 0 0 / 0.045);
  transform: translateY(-1px);
}

.mayara-template-row-active {
  border-color: rgb(var(--green-7));
  background:
    linear-gradient(
      135deg,
      rgb(var(--green-3)),
      rgb(var(--slate-1)) 78%
    );
  box-shadow:
    inset 3px 0 0 rgb(var(--green-9)),
    0 3px 10px rgb(0 0 0 / 0.045);
}

.mayara-row-main {
  min-width: 0;
}

.mayara-template-name,
.mayara-template-technical-name,
.mayara-row-preview {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.mayara-template-name {
  margin: 0;
  color: rgb(var(--slate-12));
  font-size: 0.775rem;
  font-weight: 600;
}

.mayara-template-technical-name {
  margin: 0.125rem 0 0;
  color: rgb(var(--slate-9));
  font-size: 0.6rem;
}

.mayara-row-chevron {
  width: 1rem;
  height: 1rem;
  margin-top: 0.125rem;
  color: rgb(var(--slate-9));
}

.mayara-row-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 0.3rem;
  grid-column: 1 / -1;
}

.mayara-badge {
  display: inline-flex;
  align-items: center;
  min-height: 1.25rem;
  padding: 0.075rem 0.4rem;
  border-radius: 9999px;
  font-size: 0.65rem;
  font-weight: 500;
}

.mayara-badge-language {
  background: rgb(var(--blue-3));
  color: rgb(var(--blue-11));
}

.mayara-badge-category {
  background: rgb(var(--green-3));
  color: rgb(var(--green-11));
}

.mayara-row-preview {
  grid-column: 1 / -1;
  margin: 0;
  color: rgb(var(--slate-10));
  font-size: 0.6875rem;
  line-height: 1rem;
}

.mayara-picker-preview {
  min-width: 0;
  min-height: 0;
  padding: 1.5rem;
  overflow: hidden;
  background:
    radial-gradient(
      circle at 80% 10%,
      rgb(var(--green-3) / 0.45),
      transparent 32%
    ),
    rgb(var(--slate-1));
}

.mayara-preview-layout {
  display: flex;
  height: 100%;
  min-height: 0;
  flex-direction: column;
}

.mayara-preview-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
}

.mayara-preview-eyebrow {
  margin: 0 0 0.25rem;
  color: rgb(var(--green-11));
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.mayara-preview-title {
  margin: 0;
  color: rgb(var(--slate-12));
  font-size: 1.125rem;
  font-weight: 650;
  line-height: 1.5rem;
}

.mayara-preview-technical-name {
  margin: 0.2rem 0 0;
  color: rgb(var(--slate-9));
  font-size: 0.7rem;
}

.mayara-preview-badges {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 0.375rem;
}

.mayara-phone-stage {
  display: flex;
  min-height: 0;
  flex: 1;
  align-items: center;
  justify-content: center;
  padding: 1.5rem 1rem;
}

.mayara-phone-preview {
  width: min(100%, 34rem);
  max-height: 100%;
  padding: 1rem;
  overflow-y: auto;
  border: 1px solid rgb(var(--slate-5));
  border-radius: 1rem;
  background: rgb(var(--slate-2));
  box-shadow: 0 12px 35px rgb(0 0 0 / 0.06);
}

.mayara-whatsapp-topline {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  margin-bottom: 0.75rem;
  color: rgb(var(--slate-9));
  font-size: 0.6875rem;
  font-weight: 600;
}

.mayara-whatsapp-dot {
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 999px;
  background: #22c55e;
}

.mayara-message-bubble {
  width: fit-content;
  max-width: 92%;
  margin-left: auto;
  padding: 0.875rem 1rem;
  border-radius: 1rem 1rem 0.25rem 1rem;
  background: rgb(var(--green-3));
  color: rgb(var(--slate-12));
}

.mayara-message-bubble p {
  margin: 0;
  white-space: pre-wrap;
  font-size: 0.875rem;
  line-height: 1.35rem;
}

.mayara-preview-note {
  margin: 0.75rem 0 0;
  color: rgb(var(--slate-9));
  font-size: 0.6875rem;
  text-align: center;
}

.mayara-preview-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding-top: 1rem;
  border-top: 1px solid rgb(var(--slate-5));
}

.mayara-action-help {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  color: rgb(var(--slate-9));
  font-size: 0.75rem;
}

.mayara-use-template {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  min-height: 2.5rem;
  padding: 0.55rem 1rem;
  border: 0;
  border-radius: 0.75rem;
  background: #16a34a;
  color: white;
  font-size: 0.8125rem;
  font-weight: 650;
  cursor: pointer;
}

.mayara-use-template:hover {
  background: #15803d;
}

.mayara-empty-state,
.mayara-preview-empty {
  display: flex;
  min-height: 0;
  flex: 1;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  color: rgb(var(--slate-9));
  text-align: center;
}

.mayara-empty-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.75rem;
  height: 2.75rem;
  margin-bottom: 0.75rem;
  border-radius: 0.875rem;
  background: rgb(var(--slate-3));
  color: rgb(var(--slate-10));
}

.mayara-empty-title {
  margin: 0;
  color: rgb(var(--slate-12));
  font-size: 0.9375rem;
  font-weight: 600;
}

.mayara-empty-description,
.mayara-preview-empty p {
  max-width: 22rem;
  margin: 0.375rem 0 0;
  color: rgb(var(--slate-10));
  font-size: 0.8125rem;
  line-height: 1.25rem;
}

.mayara-empty-refresh {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 1rem;
  padding: 0.5rem 0.75rem;
  border: 0;
  border-radius: 0.625rem;
  background: #16a34a;
  color: white;
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
}

.mayara-empty-refresh:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

@media (max-width: 900px) {
  .mayara-picker {
    display: flex;
    height: auto;
    max-height: 72vh;
    min-height: 0;
    flex-direction: column;
    overflow-y: auto;
  }

  .mayara-picker-sidebar {
    min-height: 18rem;
    border-right: 0;
    border-bottom: 1px solid rgb(var(--slate-5));
  }

  .mayara-template-list {
    max-height: 18rem;
  }

  .mayara-picker-preview {
    min-height: 24rem;
  }
}
</style>
